import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ConfirmModal from '../components/ConfirmModal';
import CompactListingCard from '../components/CompactListingCard';
import './ProfilePage.css';
import './MyListingsPage.css';

// Listings stay marked SOLD on the public site for this many hours after the
// agent marks them sold; after that the sweep in loadListings() flips them to
// 'archived' (hidden from public). Archived rows remain visible & restorable
// from the Archived filter on this page.
const SOLD_ARCHIVE_HOURS = 4;

function IconHome() {
  return <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
}
function IconRocket() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>;
}

function statusOf(l) {
  if (l.status === 'sold')     return 'sold';
  if (l.status === 'archived') return 'archived';
  return 'active';
}

export default function MyListingsPage() {
  const { t } = useLanguage();
  const { user, logout, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteListingId, setDeleteListingId] = useState(null);
  const [markSoldId, setMarkSoldId] = useState(null);
  const [unmarkSoldId, setUnmarkSoldId] = useState(null);
  const [boostSuccess, setBoostSuccess] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => {
    if (searchParams.get('boost') !== 'success') return;
    setBoostSuccess(true);
    try {
      sessionStorage.removeItem('boost_pkg');
      sessionStorage.removeItem('boost_selection');
      sessionStorage.removeItem('boost_selection_boost_single');
      localStorage.removeItem('boost_listing_ids');
      localStorage.removeItem('boost_package_key');
    } catch (_) {/* ignore */}
    const url = new URL(window.location.href);
    url.searchParams.delete('boost');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.pathname + (url.search || ''));
  }, [searchParams]);

  const loadListings = async () => {
    if (!user || !user.id) return;
    setLoading(true);

    // Owner-scoped sweep: flip any of this user's SOLD listings older than
    // SOLD_ARCHIVE_HOURS to 'archived'. Safe even if the status column is
    // absent — a 400 is swallowed.
    const cutoff = new Date(Date.now() - SOLD_ARCHIVE_HOURS * 60 * 60 * 1000).toISOString();
    await supabase
      .from('listings')
      .update({ status: 'archived' })
      .eq('user_id', user.id)
      .eq('status', 'sold')
      .lt('sold_at', cutoff);

    const { data } = await supabase
      .from('listings')
      .select('*')
      .eq('user_id', user.id)
      .neq('status', 'deleted')
      .order('created_at', { ascending: false });

    setListings(data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (!user || !user.id) return;
    setListings([]);
    loadListings();
  }, [user?.id]); // eslint-disable-line

  if (!user) { navigate('/login'); return null; }

  const isExpired = (date) => !date || new Date(date) < new Date();
  const daysLeft = (date) => Math.max(0, Math.ceil((new Date(date) - new Date()) / 86400000));
  const isAlreadyBoosted = (l) => l.is_boosted && !isExpired(l.boost_expires_at);

  const confirmDelete = async () => {
    const id = deleteListingId;
    setDeleteListingId(null);
    if (!id) return;
    // Soft-delete so existing buyer/seller chats can survive. The row stays
    // in place; public reads filter status='deleted' the same way they filter
    // status='archived'. Hard delete would CASCADE through conversations.
    const { error } = await supabase
      .from('listings')
      .update({ status: 'deleted', deleted_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) { alert('Failed to delete: ' + error.message); return; }
    setListings(prev => prev.filter(l => l.id !== id));
  };

  const confirmMarkSold = async () => {
    const id = markSoldId;
    setMarkSoldId(null);
    if (!id) return;
    const { error } = await supabase
      .from('listings')
      .update({ status: 'sold', sold_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) { alert('Failed to mark as sold: ' + error.message); return; }
    loadListings();
  };

  const confirmUnmarkSold = async () => {
    const id = unmarkSoldId;
    setUnmarkSoldId(null);
    if (!id) return;
    const { error } = await supabase
      .from('listings')
      .update({ status: 'active', sold_at: null })
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) { alert('Failed to restore: ' + error.message); return; }
    loadListings();
  };

  const counts = {
    all:      listings.length,
    active:   listings.filter(l => statusOf(l) === 'active').length,
    sold:     listings.filter(l => statusOf(l) === 'sold').length,
    archived: listings.filter(l => statusOf(l) === 'archived').length,
  };

  const visible = statusFilter === 'all'
    ? listings
    : listings.filter(l => statusOf(l) === statusFilter);

  const filters = [
    { key: 'active',   label: t('myListings.filterActive') },
    { key: 'sold',     label: t('myListings.filterSold') },
    { key: 'archived', label: t('myListings.filterArchived') },
    { key: 'all',      label: t('myListings.filterAll') },
  ];

  return (
    <div className="profile-page">
      <div className="profile-page__inner">
        <div className="profile-page__sidebar">
          <div className="profile-page__avatar">
            {profile?.is_broker && profile?.partner_status === 'approved' && profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : user.avatar}
          </div>
          <div className="profile-page__name">{user.firstName} {user.lastName}</div>
          <div className="profile-page__email">{user.email}</div>
          <nav className="profile-page__nav">
            <button className="profile-page__nav-item" onClick={() => navigate('/profile')}>{t('nav.myProfile')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--active" onClick={() => navigate('/my-listings')}>{t('nav.myListings')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/inbox')}>Inbox</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/settings')}>{t('nav.settings')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--logout" onClick={() => { logout(); navigate('/'); }}>{t('nav.logout')}</button>
          </nav>
        </div>

        <div className="profile-page__content">
          <div className="profile-page__section-header">
            <h1 className="profile-page__title">{t('myListings.title')}</h1>
            <p className="profile-page__sub">{t('myListings.sub')}</p>
          </div>

          {boostSuccess && (
            <div className="mylistings__boost-success" role="status">
              <span className="mylistings__boost-success-dot" aria-hidden="true" />
              Payment received — your boost is active for 14 days.
              <button
                type="button"
                className="mylistings__boost-success-close"
                onClick={() => setBoostSuccess(false)}
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          <div className="profile-page__card">
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>{t('myListings.loading')}</div>
            ) : listings.length === 0 ? (
              <div className="mylistings__empty">
                <div className="mylistings__empty-icon"><IconHome /></div>
                <h3>{t('myListings.noListings')}</h3>
                <p>{t('myListings.noListingsSub')}</p>
                <button className="profile-page__btn" onClick={() => navigate('/list-property')}>{t('myListings.postListing')}</button>
              </div>
            ) : (
              <div>
                <div className="mylistings__toolbar">
                  <span className="mylistings__count">
                    {listings.length !== 1
                      ? t('myListings.listingsCount').replace('{n}', listings.length)
                      : t('myListings.listingCount').replace('{n}', listings.length)}
                  </span>
                  <div className="mylistings__toolbar-actions">
                    <button
                      type="button"
                      className="mylistings__boost-btn"
                      onClick={() => navigate('/boost')}
                    >
                      <IconRocket /> {t('myListings.boostListings')}
                    </button>
                  </div>
                </div>

                <div className="mylistings__filters" role="tablist" aria-label="Filter listings by status">
                  {filters.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      role="tab"
                      aria-selected={statusFilter === f.key}
                      className={`mylistings__filter${statusFilter === f.key ? ' is-active' : ''}`}
                      onClick={() => setStatusFilter(f.key)}
                    >
                      {f.label} <span className="mylistings__filter-count">{counts[f.key]}</span>
                    </button>
                  ))}
                </div>

                {visible.length === 0 ? (
                  <div className="mylistings__empty-filter">{t('myListings.filterEmpty')}</div>
                ) : (
                  <div className="mylistings__grid">
                    {visible.map(l => (
                      <CompactListingCard
                        key={l.id}
                        listing={l}
                        mode="manage"
                        isAlreadyBoosted={isAlreadyBoosted(l)}
                        daysLeft={daysLeft(l.boost_expires_at)}
                        onView={() => navigate(`/property/${l.id}`)}
                        onEdit={() => navigate(`/edit-listing/${l.id}`)}
                        onDelete={() => setDeleteListingId(l.id)}
                        onMarkSold={() => setMarkSoldId(l.id)}
                        onUnmarkSold={() => setUnmarkSoldId(l.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="profile-page__card" style={{ marginTop: 16 }}>
            <h2 className="profile-page__card-title">{t('myListings.tipsTitle')}</h2>
            <div className="mylistings__tips">
              {t('myListings.tips').map((tip, i) => (
                <div key={i} className="mylistings__tip">
                  <div><div className="mylistings__tip-title">{tip.title}</div><div className="mylistings__tip-desc">{tip.desc}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteListingId !== null}
        title="Delete this listing?"
        message="The listing will be removed from the public site. Any existing chats with potential buyers remain intact so you can keep messaging them."
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteListingId(null)}
      />

      <ConfirmModal
        isOpen={markSoldId !== null}
        title={t('myListings.markSoldTitle')}
        message={t('myListings.markSoldMessage')}
        confirmLabel={t('myListings.markSoldConfirm')}
        cancelLabel="Cancel"
        onConfirm={confirmMarkSold}
        onCancel={() => setMarkSoldId(null)}
      />

      <ConfirmModal
        isOpen={unmarkSoldId !== null}
        title={t('myListings.restoreTitle')}
        message={t('myListings.restoreMessage')}
        confirmLabel={t('myListings.restoreConfirm')}
        cancelLabel="Cancel"
        onConfirm={confirmUnmarkSold}
        onCancel={() => setUnmarkSoldId(null)}
      />
    </div>
  );
}
